import { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  GitBranch,
  Plus,
  Play,
  Save,
  Trash2,
  Copy,
  TrendingUp,
  TrendingDown,
  Percent,
  DollarSign,
  BarChart3,
  RefreshCw,
  Layers,
  Calendar,
  ArrowRight,
  Check,
  X
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, Cell } from 'recharts';

// Quarters definition for fiscal year (Jul-Jun)
const QUARTERS = [
  { key: 'q1', label: 'Q1', months: ['Jul', 'Aug', 'Sep'], monthKeys: ['jul', 'aug', 'sep'] },
  { key: 'q2', label: 'Q2', months: ['Oct', 'Nov', 'Dec'], monthKeys: ['oct', 'nov', 'dec'] },
  { key: 'q3', label: 'Q3', months: ['Jan', 'Feb', 'Mar'], monthKeys: ['jan', 'feb', 'mar'] },
  { key: 'q4', label: 'Q4', months: ['Apr', 'May', 'Jun'], monthKeys: ['apr', 'may', 'jun'] }
];

const MONTHS = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

// Sample scenarios with quarterly breakdown
const SAMPLE_SCENARIOS = [
  {
    id: '1',
    name: 'Base Case',
    description: 'Current budget projections with no changes',
    is_baseline: true,
    useQuarterly: false,
    parameters: {
      revenue_growth: 0,
      expense_change: 0,
      inflation_rate: 5.5,
      quarterly: {
        q1: { revenue: 0, expense: 0 },
        q2: { revenue: 0, expense: 0 },
        q3: { revenue: 0, expense: 0 },
        q4: { revenue: 0, expense: 0 }
      }
    },
    results: {
      total_revenue: 58379025,
      total_expenses: 47098586,
      net_income: 11280439,
      quarterly: {
        q1: { revenue: 14594756, expenses: 11774647, net: 2820109 },
        q2: { revenue: 14594756, expenses: 11774647, net: 2820109 },
        q3: { revenue: 14594756, expenses: 11774647, net: 2820109 },
        q4: { revenue: 14594756, expenses: 11774647, net: 2820109 }
      }
    }
  },
  {
    id: '2',
    name: 'Seasonal Growth',
    description: 'Higher growth in Q2 (holiday season), slower in Q1',
    is_baseline: false,
    useQuarterly: true,
    parameters: {
      revenue_growth: 8,
      expense_change: 3,
      inflation_rate: 5.5,
      quarterly: {
        q1: { revenue: 2, expense: 2 },
        q2: { revenue: 18, expense: 5 },
        q3: { revenue: 8, expense: 3 },
        q4: { revenue: 5, expense: 2 }
      }
    },
    results: {
      total_revenue: 63049347,
      total_expenses: 48511544,
      net_income: 14537803,
      quarterly: {
        q1: { revenue: 14886651, expenses: 12010100, net: 2876551 },
        q2: { revenue: 17221812, expenses: 12363379, net: 4858433 },
        q3: { revenue: 15762336, expenses: 12127885, net: 3634451 },
        q4: { revenue: 15324496, expenses: 12010100, net: 3314396 }
      }
    }
  },
  {
    id: '3',
    name: 'Conservative',
    description: 'Flat revenue with cost reduction focus',
    is_baseline: false,
    useQuarterly: false,
    parameters: {
      revenue_growth: -2,
      expense_change: -5,
      inflation_rate: 6,
      quarterly: {
        q1: { revenue: -2, expense: -5 },
        q2: { revenue: -2, expense: -5 },
        q3: { revenue: -2, expense: -5 },
        q4: { revenue: -2, expense: -5 }
      }
    },
    results: {
      total_revenue: 57211445,
      total_expenses: 44743657,
      net_income: 12467788,
      quarterly: {
        q1: { revenue: 14302861, expenses: 11185914, net: 3116947 },
        q2: { revenue: 14302861, expenses: 11185914, net: 3116947 },
        q3: { revenue: 14302861, expenses: 11185914, net: 3116947 },
        q4: { revenue: 14302861, expenses: 11185914, net: 3116947 }
      }
    }
  }
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'R 0.00';
  const absValue = Math.abs(value);
  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absValue);
  return value < 0 ? `(${formatted})` : formatted;
};

const formatCurrencyShort = (value) => {
  if (value === null || value === undefined) return 'R0';
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return `R${(value / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `R${(value / 1000).toFixed(0)}K`;
  }
  return `R${value.toFixed(0)}`;
};

const formatPercent = (value) => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const SCENARIO_COLORS = ['#059669', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const WhatIfPage = () => {
  const [scenarios, setScenarios] = useState(SAMPLE_SCENARIOS);
  const [selectedScenario, setSelectedScenario] = useState(SAMPLE_SCENARIOS[0]);
  const [activeTab, setActiveTab] = useState('parameters');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [comparisonScenarios, setComparisonScenarios] = useState(['1', '2']);
  
  // Toggle for quarterly vs linear
  const [useQuarterly, setUseQuarterly] = useState(false);
  
  // Linear parameters
  const [revenueGrowth, setRevenueGrowth] = useState(0);
  const [expenseChange, setExpenseChange] = useState(0);
  const [inflationRate, setInflationRate] = useState(5.5);
  
  // Quarterly parameters
  const [quarterlyParams, setQuarterlyParams] = useState({
    q1: { revenue: 0, expense: 0 },
    q2: { revenue: 0, expense: 0 },
    q3: { revenue: 0, expense: 0 },
    q4: { revenue: 0, expense: 0 }
  });
  
  const { isDark } = useTheme();
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (selectedScenario) {
      setRevenueGrowth(selectedScenario.parameters.revenue_growth);
      setExpenseChange(selectedScenario.parameters.expense_change);
      setInflationRate(selectedScenario.parameters.inflation_rate);
      setUseQuarterly(selectedScenario.useQuarterly || false);
      if (selectedScenario.parameters.quarterly) {
        setQuarterlyParams(selectedScenario.parameters.quarterly);
      }
    }
  }, [selectedScenario]);

  const baseline = scenarios.find(s => s.is_baseline);

  // Calculate projected results based on current parameters
  const calculateResults = () => {
    const baseRevenue = baseline?.results.total_revenue || 58379025;
    const baseExpenses = baseline?.results.total_expenses || 47098586;
    const quarterlyBase = baseRevenue / 4;
    const quarterlyExpenseBase = baseExpenses / 4;
    
    let projectedRevenue, projectedExpenses;
    const quarterlyResults = {};
    
    if (useQuarterly) {
      // Calculate each quarter separately
      projectedRevenue = 0;
      projectedExpenses = 0;
      
      QUARTERS.forEach(q => {
        const qRevGrowth = quarterlyParams[q.key]?.revenue || 0;
        const qExpChange = quarterlyParams[q.key]?.expense || 0;
        const qRev = quarterlyBase * (1 + qRevGrowth / 100);
        const qExp = quarterlyExpenseBase * (1 + qExpChange / 100);
        
        quarterlyResults[q.key] = {
          revenue: qRev,
          expenses: qExp,
          net: qRev - qExp
        };
        
        projectedRevenue += qRev;
        projectedExpenses += qExp;
      });
    } else {
      // Linear calculation
      projectedRevenue = baseRevenue * (1 + revenueGrowth / 100);
      projectedExpenses = baseExpenses * (1 + expenseChange / 100);
      
      const qRev = projectedRevenue / 4;
      const qExp = projectedExpenses / 4;
      QUARTERS.forEach(q => {
        quarterlyResults[q.key] = {
          revenue: qRev,
          expenses: qExp,
          net: qRev - qExp
        };
      });
    }
    
    const projectedNetIncome = projectedRevenue - projectedExpenses;
    
    return {
      total_revenue: projectedRevenue,
      total_expenses: projectedExpenses,
      net_income: projectedNetIncome,
      quarterly: quarterlyResults
    };
  };

  const projectedResults = calculateResults();

  const runSimulation = () => {
    setIsSimulating(true);
    setTimeout(() => {
      const results = calculateResults();
      setScenarios(prev => prev.map(s => 
        s.id === selectedScenario.id 
          ? { 
              ...s, 
              useQuarterly,
              parameters: { 
                revenue_growth: revenueGrowth, 
                expense_change: expenseChange, 
                inflation_rate: inflationRate,
                quarterly: quarterlyParams
              },
              results 
            }
          : s
      ));
      setSelectedScenario(prev => ({
        ...prev,
        useQuarterly,
        parameters: { 
          revenue_growth: revenueGrowth, 
          expense_change: expenseChange, 
          inflation_rate: inflationRate,
          quarterly: quarterlyParams
        },
        results
      }));
      setIsSimulating(false);
      toast.success('Simulation complete');
    }, 1000);
  };

  const createNewScenario = (name, description) => {
    const newScenario = {
      id: Date.now().toString(),
      name,
      description,
      is_baseline: false,
      useQuarterly: false,
      parameters: { 
        revenue_growth: 0, 
        expense_change: 0, 
        inflation_rate: 5.5,
        quarterly: {
          q1: { revenue: 0, expense: 0 },
          q2: { revenue: 0, expense: 0 },
          q3: { revenue: 0, expense: 0 },
          q4: { revenue: 0, expense: 0 }
        }
      },
      results: { ...baseline?.results }
    };
    setScenarios(prev => [...prev, newScenario]);
    setSelectedScenario(newScenario);
    setDialogOpen(false);
    toast.success('Scenario created');
  };

  const duplicateScenario = (scenario) => {
    const newScenario = {
      ...scenario,
      id: Date.now().toString(),
      name: `${scenario.name} (Copy)`,
      is_baseline: false
    };
    setScenarios(prev => [...prev, newScenario]);
    toast.success('Scenario duplicated');
  };

  const deleteScenario = (id) => {
    if (scenarios.find(s => s.id === id)?.is_baseline) {
      toast.error('Cannot delete baseline scenario');
      return;
    }
    setScenarios(prev => prev.filter(s => s.id !== id));
    if (selectedScenario?.id === id) {
      setSelectedScenario(baseline);
    }
    toast.success('Scenario deleted');
  };

  const toggleComparisonScenario = (id) => {
    setComparisonScenarios(prev => {
      if (prev.includes(id)) {
        return prev.filter(s => s !== id);
      }
      if (prev.length >= 4) {
        toast.error('Maximum 4 scenarios for comparison');
        return prev;
      }
      return [...prev, id];
    });
  };

  // Quarterly chart data
  const quarterlyChartData = QUARTERS.map(q => ({
    quarter: q.label,
    ...scenarios.reduce((acc, s) => ({
      ...acc,
      [`${s.name}_revenue`]: s.results.quarterly?.[q.key]?.revenue || s.results.total_revenue / 4,
      [`${s.name}_net`]: s.results.quarterly?.[q.key]?.net || s.results.net_income / 4
    }), {})
  }));

  // Monthly projection data
  const monthlyData = MONTHS.map((month, i) => {
    const quarterIndex = Math.floor(i / 3);
    const quarterKey = QUARTERS[quarterIndex].key;
    const baseMonthlyRevenue = (baseline?.results.total_revenue || 0) / 12;
    
    let projectedMonthlyRevenue;
    if (useQuarterly) {
      const qRevGrowth = quarterlyParams[quarterKey]?.revenue || 0;
      projectedMonthlyRevenue = baseMonthlyRevenue * (1 + qRevGrowth / 100);
    } else {
      projectedMonthlyRevenue = (projectedResults.total_revenue) / 12;
    }
    
    return {
      month,
      quarter: QUARTERS[quarterIndex].label,
      baseline: baseMonthlyRevenue / 1000000,
      projected: projectedMonthlyRevenue / 1000000
    };
  });

  // Comparison data for scenarios
  const comparisonScenariosData = scenarios.filter(s => comparisonScenarios.includes(s.id));

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="whatif-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`font-display text-3xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
            What-If Scenarios
          </h1>
          <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>
            Model different financial scenarios with quarterly or annual projections
          </p>
        </div>
        {hasPermission('write') && (
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
            data-testid="create-scenario-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
            New Scenario
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Scenarios List */}
        <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
          <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
            <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
              Scenarios
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="space-y-1">
              {scenarios.map((scenario, idx) => (
                <div 
                  key={scenario.id}
                  className={`p-3 rounded-sm cursor-pointer transition-colors ${
                    selectedScenario?.id === scenario.id
                      ? isDark ? 'bg-zinc-800' : 'bg-slate-100'
                      : isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-slate-50'
                  }`}
                  onClick={() => setSelectedScenario(scenario)}
                  data-testid={`scenario-${scenario.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: SCENARIO_COLORS[idx % SCENARIO_COLORS.length] }}
                      />
                      <div>
                        <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          {scenario.name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {scenario.is_baseline && (
                            <span className="text-xs text-emerald-600">Baseline</span>
                          )}
                          {scenario.useQuarterly && (
                            <Badge variant="outline" className="text-xs py-0 h-4">Quarterly</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {!scenario.is_baseline && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={(e) => { e.stopPropagation(); duplicateScenario(scenario); }}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-red-600"
                          onClick={(e) => { e.stopPropagation(); deleteScenario(scenario.id); }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                    {scenario.description}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {selectedScenario && (
            <>
              {/* Scenario Header */}
              <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className={`text-xl font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {selectedScenario.name}
                      </h2>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {selectedScenario.description}
                      </p>
                    </div>
                    {!selectedScenario.is_baseline && (
                      <Button 
                        onClick={runSimulation}
                        disabled={isSimulating}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                        data-testid="run-simulation-btn"
                      >
                        {isSimulating ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="w-4 h-4 mr-2" />
                        )}
                        {isSimulating ? 'Running...' : 'Run Simulation'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Results Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <DollarSign className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                      {baseline && (
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          projectedResults.total_revenue >= baseline.results.total_revenue
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {formatPercent((projectedResults.total_revenue / baseline.results.total_revenue - 1) * 100)}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm mt-2 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Projected Revenue</p>
                    <p className={`font-mono text-xl font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(projectedResults.total_revenue)}
                    </p>
                  </CardContent>
                </Card>

                <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <TrendingDown className="w-5 h-5 text-red-600" strokeWidth={1.5} />
                      {baseline && (
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          projectedResults.total_expenses <= baseline.results.total_expenses
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {formatPercent((projectedResults.total_expenses / baseline.results.total_expenses - 1) * 100)}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm mt-2 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Projected Expenses</p>
                    <p className={`font-mono text-xl font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(projectedResults.total_expenses)}
                    </p>
                  </CardContent>
                </Card>

                <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <TrendingUp className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                      {baseline && (
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          projectedResults.net_income >= baseline.results.net_income
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {formatPercent((projectedResults.net_income / baseline.results.net_income - 1) * 100)}
                        </span>
                      )}
                    </div>
                    <p className={`text-sm mt-2 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Projected Net Income</p>
                    <p className={`font-mono text-xl font-medium ${
                      projectedResults.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(projectedResults.net_income)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Parameters & Charts */}
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className={`p-1 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
                  <TabsTrigger value="parameters" className="rounded-sm">
                    Parameters
                  </TabsTrigger>
                  <TabsTrigger value="quarterly" className="rounded-sm">
                    Quarterly View
                  </TabsTrigger>
                  <TabsTrigger value="projection" className="rounded-sm">
                    Monthly Projection
                  </TabsTrigger>
                  <TabsTrigger value="comparison" className="rounded-sm">
                    Compare Scenarios
                  </TabsTrigger>
                </TabsList>

                {/* Parameters Tab */}
                <TabsContent value="parameters" className="mt-4">
                  <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                    <CardContent className="p-6 space-y-6">
                      {selectedScenario.is_baseline ? (
                        <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>
                          Baseline scenario cannot be modified. Create a new scenario to model changes.
                        </p>
                      ) : (
                        <>
                          {/* Mode Toggle */}
                          <div className={`p-4 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <Label className="text-base font-medium">Quarterly Input Mode</Label>
                                <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                  {useQuarterly 
                                    ? 'Set different growth rates for each quarter' 
                                    : 'Apply uniform growth across all quarters'}
                                </p>
                              </div>
                              <Switch 
                                checked={useQuarterly} 
                                onCheckedChange={setUseQuarterly}
                                data-testid="quarterly-toggle"
                              />
                            </div>
                          </div>

                          {!useQuarterly ? (
                            /* Linear Parameters */
                            <div className="space-y-6">
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                                    Revenue Growth (Annual)
                                  </Label>
                                  <span className={`font-mono ${revenueGrowth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {formatPercent(revenueGrowth)}
                                  </span>
                                </div>
                                <Slider
                                  value={[revenueGrowth]}
                                  onValueChange={([v]) => setRevenueGrowth(v)}
                                  min={-30}
                                  max={50}
                                  step={0.5}
                                  className="w-full"
                                  data-testid="revenue-slider"
                                />
                              </div>

                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="flex items-center gap-2">
                                    <TrendingDown className="w-4 h-4 text-red-600" />
                                    Expense Change (Annual)
                                  </Label>
                                  <span className={`font-mono ${expenseChange <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {formatPercent(expenseChange)}
                                  </span>
                                </div>
                                <Slider
                                  value={[expenseChange]}
                                  onValueChange={([v]) => setExpenseChange(v)}
                                  min={-20}
                                  max={30}
                                  step={0.5}
                                  className="w-full"
                                  data-testid="expense-slider"
                                />
                              </div>

                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="flex items-center gap-2">
                                    <Percent className="w-4 h-4 text-amber-600" />
                                    Inflation Rate
                                  </Label>
                                  <span className="font-mono text-amber-600">
                                    {inflationRate.toFixed(1)}%
                                  </span>
                                </div>
                                <Slider
                                  value={[inflationRate]}
                                  onValueChange={([v]) => setInflationRate(v)}
                                  min={2}
                                  max={15}
                                  step={0.1}
                                  className="w-full"
                                  data-testid="inflation-slider"
                                />
                              </div>
                            </div>
                          ) : (
                            /* Quarterly Parameters */
                            <div className="space-y-4">
                              <div className="grid grid-cols-4 gap-4">
                                {QUARTERS.map(q => (
                                  <div 
                                    key={q.key} 
                                    className={`p-4 rounded-sm border ${isDark ? 'bg-zinc-800/50 border-zinc-700' : 'bg-white border-slate-200'}`}
                                  >
                                    <div className="flex items-center gap-2 mb-3">
                                      <Calendar className="w-4 h-4 text-violet-600" />
                                      <span className="font-medium">{q.label}</span>
                                      <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                        ({q.months.join('-')})
                                      </span>
                                    </div>
                                    
                                    <div className="space-y-3">
                                      <div>
                                        <Label className="text-xs flex items-center gap-1">
                                          <TrendingUp className="w-3 h-3 text-emerald-600" />
                                          Revenue
                                        </Label>
                                        <div className="flex items-center gap-2 mt-1">
                                          <Input
                                            type="number"
                                            value={quarterlyParams[q.key]?.revenue || 0}
                                            onChange={(e) => setQuarterlyParams(prev => ({
                                              ...prev,
                                              [q.key]: { ...prev[q.key], revenue: parseFloat(e.target.value) || 0 }
                                            }))}
                                            className={`h-8 text-sm ${isDark ? 'bg-zinc-700 border-zinc-600' : ''}`}
                                            data-testid={`${q.key}-revenue-input`}
                                          />
                                          <span className="text-xs">%</span>
                                        </div>
                                      </div>
                                      
                                      <div>
                                        <Label className="text-xs flex items-center gap-1">
                                          <TrendingDown className="w-3 h-3 text-red-600" />
                                          Expense
                                        </Label>
                                        <div className="flex items-center gap-2 mt-1">
                                          <Input
                                            type="number"
                                            value={quarterlyParams[q.key]?.expense || 0}
                                            onChange={(e) => setQuarterlyParams(prev => ({
                                              ...prev,
                                              [q.key]: { ...prev[q.key], expense: parseFloat(e.target.value) || 0 }
                                            }))}
                                            className={`h-8 text-sm ${isDark ? 'bg-zinc-700 border-zinc-600' : ''}`}
                                            data-testid={`${q.key}-expense-input`}
                                          />
                                          <span className="text-xs">%</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Quarterly Summary */}
                              <div className={`p-4 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                                <p className={`text-sm font-medium mb-3 ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                  Quarterly Impact Preview
                                </p>
                                <div className="grid grid-cols-4 gap-4 text-center">
                                  {QUARTERS.map(q => {
                                    const qResults = projectedResults.quarterly?.[q.key];
                                    return (
                                      <div key={q.key}>
                                        <p className="text-xs text-zinc-500">{q.label}</p>
                                        <p className={`font-mono text-sm font-medium ${
                                          (qResults?.net || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'
                                        }`}>
                                          {formatCurrencyShort(qResults?.net || 0)}
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Quarterly View Tab */}
                <TabsContent value="quarterly" className="mt-4">
                  <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                    <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                      <CardTitle className="font-display text-base font-medium">Quarterly Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className={`border-b ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                              <th className="text-left p-3 font-medium">Quarter</th>
                              <th className="text-left p-3 font-medium">Period</th>
                              <th className="text-right p-3 font-medium">Revenue</th>
                              <th className="text-right p-3 font-medium">Expenses</th>
                              <th className="text-right p-3 font-medium">Net Income</th>
                              <th className="text-right p-3 font-medium">Margin</th>
                            </tr>
                          </thead>
                          <tbody>
                            {QUARTERS.map(q => {
                              const qResults = projectedResults.quarterly?.[q.key];
                              const rev = qResults?.revenue || 0;
                              const exp = qResults?.expenses || 0;
                              const net = qResults?.net || 0;
                              const margin = rev > 0 ? (net / rev * 100) : 0;
                              
                              return (
                                <tr key={q.key} className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                                  <td className={`p-3 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                    {q.label}
                                  </td>
                                  <td className={`p-3 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                    {q.months.join(' - ')}
                                  </td>
                                  <td className="p-3 text-right font-mono text-emerald-600">
                                    {formatCurrency(rev)}
                                  </td>
                                  <td className="p-3 text-right font-mono text-red-600">
                                    {formatCurrency(exp)}
                                  </td>
                                  <td className={`p-3 text-right font-mono font-medium ${net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {formatCurrency(net)}
                                  </td>
                                  <td className={`p-3 text-right font-mono ${margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {margin.toFixed(1)}%
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Total Row */}
                            <tr className={`${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                              <td colSpan={2} className={`p-3 font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                Full Year Total
                              </td>
                              <td className="p-3 text-right font-mono font-bold text-emerald-600">
                                {formatCurrency(projectedResults.total_revenue)}
                              </td>
                              <td className="p-3 text-right font-mono font-bold text-red-600">
                                {formatCurrency(projectedResults.total_expenses)}
                              </td>
                              <td className={`p-3 text-right font-mono font-bold ${projectedResults.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatCurrency(projectedResults.net_income)}
                              </td>
                              <td className={`p-3 text-right font-mono font-bold ${
                                projectedResults.total_revenue > 0 && projectedResults.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'
                              }`}>
                                {projectedResults.total_revenue > 0 
                                  ? (projectedResults.net_income / projectedResults.total_revenue * 100).toFixed(1)
                                  : 0}%
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Quarterly Bar Chart */}
                      <div className="h-64 mt-6">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={QUARTERS.map(q => ({
                            quarter: q.label,
                            revenue: (projectedResults.quarterly?.[q.key]?.revenue || 0) / 1000000,
                            expenses: (projectedResults.quarterly?.[q.key]?.expenses || 0) / 1000000,
                            net: (projectedResults.quarterly?.[q.key]?.net || 0) / 1000000
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#3f3f46' : '#E5E7EB'} vertical={false} />
                            <XAxis dataKey="quarter" tick={{ fontSize: 12, fill: isDark ? '#a1a1aa' : '#6B7280' }} />
                            <YAxis tick={{ fontSize: 12, fill: isDark ? '#a1a1aa' : '#6B7280' }} tickFormatter={(v) => `R${v}M`} />
                            <Tooltip 
                              formatter={(value) => [`R ${value.toFixed(1)}M`, '']}
                              contentStyle={{ 
                                borderRadius: '2px',
                                backgroundColor: isDark ? '#18181b' : '#fff',
                                border: isDark ? '1px solid #3f3f46' : '1px solid #E5E7EB'
                              }}
                            />
                            <Legend />
                            <Bar dataKey="revenue" name="Revenue" fill="#059669" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="expenses" name="Expenses" fill="#ef4444" radius={[2, 2, 0, 0]} />
                            <Bar dataKey="net" name="Net Income" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Monthly Projection Tab */}
                <TabsContent value="projection" className="mt-4">
                  <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                    <CardContent className="p-6">
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#3f3f46' : '#E5E7EB'} vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 12, fill: isDark ? '#a1a1aa' : '#6B7280' }} />
                            <YAxis 
                              tick={{ fontSize: 12, fill: isDark ? '#a1a1aa' : '#6B7280' }} 
                              tickFormatter={(v) => `R${v}M`}
                            />
                            <Tooltip 
                              formatter={(value) => [`R ${value.toFixed(2)}M`, '']}
                              contentStyle={{ 
                                borderRadius: '2px',
                                backgroundColor: isDark ? '#18181b' : '#fff',
                                border: isDark ? '1px solid #3f3f46' : '1px solid #E5E7EB'
                              }}
                            />
                            <Legend />
                            <Line 
                              type="monotone" 
                              dataKey="baseline" 
                              stroke="#6B7280" 
                              strokeWidth={2}
                              strokeDasharray="5 5"
                              dot={false}
                              name="Baseline"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="projected" 
                              stroke="#059669" 
                              strokeWidth={2}
                              dot={false}
                              name="Projected"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      
                      {/* Show quarter indicators */}
                      <div className="flex justify-around mt-4 pt-4 border-t border-dashed">
                        {QUARTERS.map(q => (
                          <div key={q.key} className="text-center">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-100 text-slate-600'}`}>
                              {q.label}: {q.months[0]} - {q.months[2]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Comparison Tab */}
                <TabsContent value="comparison" className="mt-4 space-y-4">
                  {/* Scenario Selection */}
                  <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                    <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                      <CardTitle className="font-display text-base font-medium">Select Scenarios to Compare</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {scenarios.map((scenario, idx) => (
                          <button
                            key={scenario.id}
                            onClick={() => toggleComparisonScenario(scenario.id)}
                            className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors flex items-center gap-2 ${
                              comparisonScenarios.includes(scenario.id)
                                ? 'text-white'
                                : isDark ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            style={comparisonScenarios.includes(scenario.id) ? { backgroundColor: SCENARIO_COLORS[idx % SCENARIO_COLORS.length] } : {}}
                            data-testid={`compare-${scenario.id}`}
                          >
                            {comparisonScenarios.includes(scenario.id) ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <Plus className="w-3 h-3" />
                            )}
                            {scenario.name}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Comparison Table */}
                  <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                    <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                      <CardTitle className="font-display text-base font-medium flex items-center gap-2">
                        <Layers className="w-4 h-4 text-violet-600" />
                        Side-by-Side Comparison
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className={`border-b ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                              <th className="text-left p-3 font-medium">Metric</th>
                              {comparisonScenariosData.map((scenario, idx) => (
                                <th key={scenario.id} className="text-right p-3 font-medium">
                                  <div className="flex items-center justify-end gap-2">
                                    <div 
                                      className="w-2 h-2 rounded-full" 
                                      style={{ backgroundColor: SCENARIO_COLORS[scenarios.findIndex(s => s.id === scenario.id) % SCENARIO_COLORS.length] }}
                                    />
                                    {scenario.name}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`p-3 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                Total Revenue
                              </td>
                              {comparisonScenariosData.map(scenario => (
                                <td key={scenario.id} className="p-3 text-right font-mono text-emerald-600">
                                  {formatCurrency(scenario.results.total_revenue)}
                                </td>
                              ))}
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`p-3 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                Total Expenses
                              </td>
                              {comparisonScenariosData.map(scenario => (
                                <td key={scenario.id} className="p-3 text-right font-mono text-red-600">
                                  {formatCurrency(scenario.results.total_expenses)}
                                </td>
                              ))}
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`p-3 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                Net Income
                              </td>
                              {comparisonScenariosData.map(scenario => (
                                <td key={scenario.id} className={`p-3 text-right font-mono font-medium ${
                                  scenario.results.net_income >= 0 ? 'text-emerald-600' : 'text-red-600'
                                }`}>
                                  {formatCurrency(scenario.results.net_income)}
                                </td>
                              ))}
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`p-3 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                Profit Margin
                              </td>
                              {comparisonScenariosData.map(scenario => {
                                const margin = scenario.results.total_revenue > 0 
                                  ? (scenario.results.net_income / scenario.results.total_revenue * 100)
                                  : 0;
                                return (
                                  <td key={scenario.id} className={`p-3 text-right font-mono ${margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {margin.toFixed(1)}%
                                  </td>
                                );
                              })}
                            </tr>
                            <tr className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                              <td className={`p-3 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                vs Baseline
                              </td>
                              {comparisonScenariosData.map(scenario => {
                                const vsBaseline = baseline 
                                  ? ((scenario.results.net_income / baseline.results.net_income) - 1) * 100
                                  : 0;
                                return (
                                  <td key={scenario.id} className={`p-3 text-right font-mono ${
                                    scenario.is_baseline ? '' : vsBaseline >= 0 ? 'text-emerald-600' : 'text-red-600'
                                  }`}>
                                    {scenario.is_baseline ? '-' : formatPercent(vsBaseline)}
                                  </td>
                                );
                              })}
                            </tr>
                            
                            {/* Quarterly Breakdown */}
                            <tr className={isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}>
                              <td colSpan={comparisonScenariosData.length + 1} className={`p-3 font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                                Quarterly Net Income
                              </td>
                            </tr>
                            {QUARTERS.map(q => (
                              <tr key={q.key} className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                                <td className={`p-3 pl-6 ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                                  {q.label} ({q.months[0]}-{q.months[2]})
                                </td>
                                {comparisonScenariosData.map(scenario => {
                                  const qNet = scenario.results.quarterly?.[q.key]?.net || scenario.results.net_income / 4;
                                  return (
                                    <td key={scenario.id} className={`p-3 text-right font-mono text-sm ${
                                      qNet >= 0 ? 'text-emerald-600' : 'text-red-600'
                                    }`}>
                                      {formatCurrencyShort(qNet)}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Comparison Chart */}
                  <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
                    <CardHeader className={`py-3 border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                      <CardTitle className="font-display text-base font-medium">Net Income by Quarter</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={QUARTERS.map(q => {
                            const data = { quarter: q.label };
                            comparisonScenariosData.forEach(s => {
                              data[s.name] = (s.results.quarterly?.[q.key]?.net || s.results.net_income / 4) / 1000000;
                            });
                            return data;
                          })}>
                            <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#3f3f46' : '#E5E7EB'} vertical={false} />
                            <XAxis dataKey="quarter" tick={{ fontSize: 12, fill: isDark ? '#a1a1aa' : '#6B7280' }} />
                            <YAxis tick={{ fontSize: 12, fill: isDark ? '#a1a1aa' : '#6B7280' }} tickFormatter={(v) => `R${v}M`} />
                            <Tooltip 
                              formatter={(value) => [`R ${value.toFixed(1)}M`, '']}
                              contentStyle={{ 
                                borderRadius: '2px',
                                backgroundColor: isDark ? '#18181b' : '#fff',
                                border: isDark ? '1px solid #3f3f46' : '1px solid #E5E7EB'
                              }}
                            />
                            <Legend />
                            {comparisonScenariosData.map((scenario, idx) => (
                              <Bar 
                                key={scenario.id} 
                                dataKey={scenario.name} 
                                fill={SCENARIO_COLORS[scenarios.findIndex(s => s.id === scenario.id) % SCENARIO_COLORS.length]}
                                radius={[2, 2, 0, 0]}
                              />
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>

      {/* Create Scenario Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`rounded-sm max-w-md ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
          <DialogHeader>
            <DialogTitle>Create New Scenario</DialogTitle>
          </DialogHeader>
          <CreateScenarioForm 
            onSubmit={createNewScenario}
            onCancel={() => setDialogOpen(false)}
            isDark={isDark}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CreateScenarioForm = ({ onSubmit, onCancel, isDark }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a scenario name');
      return;
    }
    onSubmit(name, description);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Scenario Name</Label>
        <Input 
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., High Growth Scenario"
          className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
          data-testid="scenario-name-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Description (Optional)</Label>
        <Input 
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of this scenario"
          className={`rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
          data-testid="scenario-description-input"
        />
      </div>
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
          Cancel
        </Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="create-scenario-submit">
          Create Scenario
        </Button>
      </DialogFooter>
    </form>
  );
};

export default WhatIfPage;
